package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Layout
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.billing.BillingStatusDto
import co.maytes.standupbot.shared.dto.billing.SubscriptionStatusDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.dom.Div

object BillingStripClasses {
    const val STRIP = "sb-billing-strip"
}

private val stackedMedia = CSSMediaQuery.Raw("(max-width: ${Layout.breakpointMd - 1}px)")

@InitSilk
fun initBillingStripStyles(ctx: InitSilkContext) {
    ctx.stylesheet.registerStyle(".${BillingStripClasses.STRIP}") {
        base {
            Modifier.styleModifier {
                property("display", "grid")
                property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
            }
        }
        cssRule(stackedMedia, null) {
            Modifier.styleModifier { property("grid-template-columns", "minmax(0, 1fr)") }
        }
    }
    ctx.stylesheet.registerStyle(".${BillingStripClasses.STRIP} > * + *") {
        base {
            Modifier.styleModifier { property("border-left", "1px solid ${Palette.borderSubtle.cssRgb()}") }
        }
        cssRule(stackedMedia, null) {
            Modifier.styleModifier {
                property("border-left", "none")
                property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
            }
        }
    }
}

@Composable
fun BillingStrip(status: BillingStatusDto) {
    val plan = status.plan
    val count = status.memberCount
    val cap = BillingCopy.memberCap(plan)
    val fraction = BillingCopy.memberFraction(plan, count)

    StripFrame {
        StripCell(label = "Plan") {
            StripValue(
                text = BillingCopy.planLabel(plan),
                trailing = { Badge(label = BillingCopy.statusLabel(status.status), tone = statusTone(status.status)) },
            )
            StripNote(BillingCopy.tagline(plan), Palette.textSecondary)
            BillingCopy.planSourceNote(status.planSource)?.let { StripNote(it, Palette.textPrimary) }
        }
        StripCell(label = "Members") {
            StripValue(text = count.toString(), qualifier = cap?.let { "of $it" })
            if (fraction != null && cap != null) UsageMeter(fraction = fraction, value = count, max = cap)
            StripNote(BillingCopy.memberHeadroom(plan, count), Palette.textSecondary)
        }
        StripCell(label = BillingCopy.renewalLabel(status)) {
            val date = BillingCopy.renewalDate(status)
            if (date != null) StripValue(text = date) else StripMissing()
            BillingCopy.renewalNote(status)?.let { StripNote(it, Palette.textSecondary) }
        }
    }
}

@Composable
fun BillingStripLoading() {
    StripFrame {
        listOf("Plan", "Members", "Renewal").forEach { label ->
            StripCell(label = label) { SettingsBars(rows = 2) }
        }
    }
}

@Composable
private fun StripFrame(content: @Composable () -> Unit) {
    Div(
        attrs = Modifier
            .fillMaxWidth()
            .classNames(BillingStripClasses.STRIP, Motion.ENTER_CLASS)
            .borderRadius(Radius.lg)
            .margin(bottom = Spacing.xs)
            .styleModifier {
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("overflow", "hidden")
            }
            .toAttrs(),
    ) { content() }
}

@Composable
private fun StripCell(label: String, content: @Composable () -> Unit) {
    Column(
        modifier = Modifier
            .padding(topBottom = Spacing.md, leftRight = Spacing.lg)
            .styleModifier {
                property("min-width", "0")
                property("gap", Spacing.xxs.toString())
            },
    ) {
        SpanText(
            text = label,
            modifier = Modifier.fontSize(Typography.label.size).fontWeight(400).color(Palette.textSecondary),
        )
        content()
    }
}

@Composable
private fun StripValue(
    text: String,
    qualifier: String? = null,
    trailing: (@Composable () -> Unit)? = null,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
    ) {
        SpanText(
            text = text,
            modifier = Modifier
                .fontSize(Typography.h2.size)
                .fontWeight(500)
                .color(Palette.textPrimary)
                .styleModifier {
                    property("letter-spacing", "-0.01em")
                    property("font-variant-numeric", "tabular-nums")
                },
        )
        if (qualifier != null) {
            SpanText(
                text = qualifier,
                modifier = Modifier.fontSize(Typography.label.size).fontWeight(400).color(Palette.textTertiary),
            )
        }
        trailing?.invoke()
    }
}

@Composable
private fun StripMissing() {
    SpanText(
        text = "–",
        modifier = Modifier.fontSize(Typography.h2.size).fontWeight(500).color(Palette.textTertiary),
    )
}

@Composable
private fun StripNote(text: String, tone: CSSColorValue) {
    SpanText(
        text = text,
        modifier = Modifier.fontSize(Typography.bodySmall.size).fontWeight(400).color(tone),
    )
}

@Composable
private fun UsageMeter(fraction: Double, value: Int, max: Int) {
    Div(
        attrs = Modifier
            .fillMaxWidth()
            .margin(topBottom = Spacing.xxs)
            .borderRadius(Radius.pill)
            .backgroundColor(Palette.border)
            .styleModifier {
                property("height", Spacing.xxs.toString())
                property("overflow", "hidden")
            }
            .toAttrs {
                attr("role", "meter")
                attr("aria-label", "Members in use")
                attr("aria-valuemin", "0")
                attr("aria-valuemax", max.toString())
                attr("aria-valuenow", value.toString())
            },
    ) {
        Div(
            attrs = Modifier
                .backgroundColor(Palette.accent)
                .borderRadius(Radius.pill)
                .styleModifier {
                    property("height", "100%")
                    property("width", "${fraction * PERCENT_SCALE}%")
                    property("transition", Motion.transition(Motion.DUR_PILL, "width"))
                }
                .toAttrs(),
        )
    }
}

private fun statusTone(status: SubscriptionStatusDto): BadgeTone = when (status) {
    SubscriptionStatusDto.ACTIVE, SubscriptionStatusDto.TRIALING -> BadgeTone.Success
    SubscriptionStatusDto.PAST_DUE, SubscriptionStatusDto.UNPAID -> BadgeTone.Warning
    SubscriptionStatusDto.CANCELED,
    SubscriptionStatusDto.INCOMPLETE,
    SubscriptionStatusDto.INCOMPLETE_EXPIRED,
    -> BadgeTone.Neutral
}

private const val PERCENT_SCALE = 100.0
